import { motion } from "framer-motion";
import { Globe2, Handshake, Package, Sparkles } from "lucide-react";
import { Footer } from "@/components/landing/Footer";
import { PageHeader } from "@/components/landing/PageHeader";
import { WholesaleForm } from "@/components/landing/WholesaleForm";

const STORE_IMG = "https://customer-assets-39nsmqrw.emergentagent.net/job_d6e5418e-7457-40d9-8c2f-98b475cb453e/artifacts/acb6a3c3b3fa5890_media-generation-deen-district-store-urban-0-70717c16-92eb-4aea-a30a-761d8e21a4eb.webp";

const PERKS = [
  { icon: Handshake, title: "Tiered wholesale pricing", text: "Margins built for independents, with better tiers as your volume grows." },
  { icon: Package, title: "Low minimums to start", text: "Open with a curated edit of Drop 001 — hoodie sets, Sister sets and the Palestine Edition." },
  { icon: Sparkles, title: "Launch support", text: "Lookbook imagery, product copy, size charts and social content ready for your channels." },
  { icon: Globe2, title: "Worldwide shipping", text: "We ship to stockists globally and help with duties paperwork where needed." },
];

const STEPS = [["Apply", "Five short sections — about 4 minutes."], ["We review", "Every stockist is reviewed personally within 3-5 business days."], ["Launch", "Line sheet, pricing and your first order — then we ship."]];

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } } };

export default function Wholesale() {
  return (
    <main data-testid="wholesale-page" className="bg-[#ffffff] text-[#0a0a0b] min-h-screen antialiased">
      <PageHeader page="wholesale" link={{ id: "contact", to: "/contact", label: "Contact" }} />

      <section className="relative pt-32 sm:pt-40 pb-16 px-5 sm:px-8 overflow-hidden">
        <div className="absolute inset-0 hero-radial" />
        <div className="relative max-w-[1200px] mx-auto grid lg:grid-cols-12 gap-10 items-end">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="lg:col-span-7">
            <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-[#ff2a85] mb-6 flex items-center gap-3"><span className="diamond" /> Wholesale · Stockists</p>
            <h1 className="font-display font-extrabold uppercase leading-[0.9] text-4xl sm:text-5xl lg:text-6xl tracking-tight">Carry <span className="text-pink">Deen District</span><br />in your store</h1>
            <p className="mt-7 text-[#5f5e5a] text-base sm:text-lg leading-relaxed max-w-xl">We partner with boutiques, online stores, community shops and distributors who share the Deen over Dunya ethos. Tell us who you are, where you are and where you'll sell — we'll come back with pricing, minimums and a launch plan.</p>
            <a href="#apply" data-testid="wholesale-apply-cta" className="mt-8 inline-flex h-13 px-8 py-4 items-center rounded-full bg-[#0a0a0b] text-white font-mono-d text-[11px] uppercase tracking-[0.2em] hover:bg-[#ff2a85] transition-colors">Apply to stock us</a>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-5 rounded-3xl overflow-hidden border border-[#e3e2dc] aspect-[4/5] lg:aspect-[4/5]">
            <img src={STORE_IMG} alt="Deen District in-store display with graffiti wall and neon sign" className="h-full w-full object-cover" loading="lazy" />
          </motion.div>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-20">
        <div className="max-w-[1200px] mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PERKS.map((p, i) => (
            <motion.div key={p.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} transition={{ delay: i * 0.08 }} data-testid={`wholesale-perk-${i}`} className="rounded-3xl border border-[#e3e2dc] bg-[#faf9f6] p-6">
              <span className="h-10 w-10 rounded-full bg-[#0a0a0b] text-white flex items-center justify-center"><p.icon size={16} /></span>
              <h3 className="mt-5 font-display font-extrabold uppercase text-sm leading-tight">{p.title}</h3>
              <p className="mt-2 text-sm text-[#5f5e5a] leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-[#0a0a0b] text-[#f5f4ee] px-5 sm:px-8 py-16 sm:py-20">
        <div className="max-w-[1200px] mx-auto grid md:grid-cols-3 gap-8">
          {STEPS.map(([t, s], i) => (
            <div key={t} data-testid={`wholesale-step-${i}`} className="border-t border-[#f5f4ee]/15 pt-5">
              <span className="font-mono-d text-[10px] text-[#ff2a85] tracking-[0.2em]">0{i + 1}</span>
              <h3 className="mt-3 font-display font-extrabold uppercase text-xl">{t}</h3>
              <p className="mt-2 text-sm text-[#f5f4ee]/65 leading-relaxed">{s}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="apply" className="px-5 sm:px-8 py-20 sm:py-28">
        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-[#ff2a85] mb-5 flex items-center gap-3"><span className="diamond" /> Application</p>
            <h2 className="font-display font-extrabold uppercase leading-[0.95] text-3xl sm:text-4xl tracking-tight">Tell us about <span className="text-pink">your store</span></h2>
            <p className="mt-5 text-[#5f5e5a] leading-relaxed">Five quick sections. The more you share about your customers and channels, the faster we can tailor pricing and minimums for you.</p>
            <p className="mt-6 text-sm text-[#5f5e5a]">Prefer email? <a href="mailto:deendistrictdrip@gmail.com" className="text-[#0a0a0b] underline underline-offset-4 hover:text-[#ff2a85]">deendistrictdrip@gmail.com</a></p>
          </div>
          <div className="lg:col-span-8"><WholesaleForm /></div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
